/**
 * Normalise an uploaded image File into a JPEG before sending to the server.
 *
 * Handles:
 *   - iPhone HEIC/HEIF photos -> auto-convert to JPEG (works on Safari iOS where
 *     the browser can natively decode HEIC; other browsers throw a clear error).
 *   - Huge camera photos (12+ MP) -> downscale to 2400px max edge to save
 *     bandwidth, speed up upload, and avoid OOM on low-end phones.
 *   - Strips any inadvertent metadata that wouldn't survive canvas re-encode.
 *
 * Returns a Promise<File> — always JPEG with .jpg extension if conversion ran,
 * or the original File if it was already small JPEG/PNG.
 */
const MAX_EDGE = 2400;
const JPEG_QUALITY = 0.88;

const isHeic = (file) => {
  const name = (file.name || '').toLowerCase();
  const type = (file.type || '').toLowerCase();
  return type === 'image/heic' || type === 'image/heif'
      || name.endsWith('.heic') || name.endsWith('.heif');
};

async function decodeToBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch (_) {
      // Fall through to <img> path
    }
  }
  // Fallback for older browsers
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
    img.src = url;
  });
}

export async function normaliseImageForUpload(file) {
  const looksHeic = isHeic(file);
  // Pass small non-HEIC images through unchanged
  if (!looksHeic && file.size < 1.5 * 1024 * 1024) return file;

  let bitmap;
  try {
    bitmap = await decodeToBitmap(file);
  } catch (e) {
    if (looksHeic) {
      throw new Error(
        'This HEIC photo couldn\'t be converted on this browser. On your iPhone, tap Share → Save as JPEG, or change Settings → Camera → Formats → Most Compatible.'
      );
    }
    throw new Error(`Could not read ${file.name} — please try a different photo.`);
  }

  // Compute target size while preserving aspect ratio
  const w = bitmap.width || bitmap.naturalWidth;
  const h = bitmap.height || bitmap.naturalHeight;
  const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
  const tw = Math.round(w * scale);
  const th = Math.round(h * scale);

  const canvas = document.createElement('canvas');
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff'; // white background for any transparent pixels
  ctx.fillRect(0, 0, tw, th);
  ctx.drawImage(bitmap, 0, 0, tw, th);

  // Free the bitmap memory ASAP
  if (typeof bitmap.close === 'function') bitmap.close();

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
  if (!blob) throw new Error('Failed to encode JPEG');

  const baseName = (file.name || 'photo').replace(/\.(heic|heif|HEIC|HEIF|png|PNG|webp|WEBP|gif|GIF)$/i, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}
